import React, { useEffect, useState, createContext, useContext } from 'react';
import { db, isFirebaseEnabled } from '../lib/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, onSnapshot, orderBy, serverTimestamp, increment, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { notifications } from '../lib/notifications';
interface Goal {
  id: string;
  title: string;
  category: 'Work' | 'Study' | 'Personal' | 'Health';
  priority: 'High' | 'Medium' | 'Low';
  completed: boolean;
  progress: number;
  dueDate?: string;
  dueTime?: string;
  createdAt: string;
  userId: string;
}
interface UserStats {
  xp: number;
  level: number;
  streak: number;
  completedGoals: number;
  focusSessions: number;
  totalFocusMinutes: number;
  achievements: string[];
}
interface UserContextType {
  goals: Goal[];
  stats: UserStats;
  loading: boolean;
  addGoal: (goal: Omit<Goal, 'id' | 'completed' | 'progress' | 'createdAt' | 'userId'>) => Promise<void>;
  updateGoal: (id: string, updates: Partial<Goal>) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  toggleGoal: (id: string) => Promise<void>;
  addXP: (amount: number) => Promise<void>;
  completeFocusSession: (duration: number) => Promise<void>;
  unlockAchievement: (achievementId: string) => Promise<void>;
}
const UserContext = createContext<UserContextType | undefined>(undefined);
const LOCAL_STORAGE_KEYS = {
  GOALS: 'quest_local_goals',
  STATS: 'quest_local_stats'
};
export function UserProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [stats, setStats] = useState<UserStats>({
    xp: 0,
    level: 1,
    streak: 0,
    completedGoals: 0,
    focusSessions: 0,
    totalFocusMinutes: 0,
    achievements: []
  });
  const [loading, setLoading] = useState(true);
  // Load from localStorage in local-only mode
  useEffect(() => {
    if (!isFirebaseEnabled || !db) {
      const savedGoals = localStorage.getItem(LOCAL_STORAGE_KEYS.GOALS);
      const savedStats = localStorage.getItem(LOCAL_STORAGE_KEYS.STATS);
      if (savedGoals) setGoals(JSON.parse(savedGoals));
      if (savedStats) setStats(JSON.parse(savedStats));
      setLoading(false);
      return;
    }
  }, []);
  // Subscribe to goals (Firebase mode)
  useEffect(() => {
    if (!isFirebaseEnabled || !db || !user) {
      if (!user) {
        setGoals([]);
        setLoading(false);
      }
      return;
    }
    const q = query(collection(db, 'goals'), where('userId', '==', user.uid), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const goalsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Goal[];
      setGoals(goalsData);
      setLoading(false);
    });
    return unsubscribe;
  }, [user]);
  // Subscribe to user stats (Firebase mode)
  useEffect(() => {
    if (!isFirebaseEnabled || !db || !user) {
      if (!user) {
        setStats({
          xp: 0,
          level: 1,
          streak: 0,
          completedGoals: 0,
          focusSessions: 0,
          totalFocusMinutes: 0,
          achievements: []
        });
      }
      return;
    }
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, doc => {
      if (doc.exists()) {
        const data = doc.data();
        setStats({
          xp: data.xp || 0,
          level: data.level || 1,
          streak: data.streak || 0,
          completedGoals: data.completedGoals || 0,
          focusSessions: data.focusSessions || 0,
          totalFocusMinutes: data.totalFocusMinutes || 0,
          achievements: data.achievements || []
        });
      }
    });
    return unsubscribe;
  }, [user]);
  // Save to localStorage when data changes (local-only mode)
  useEffect(() => {
    if (!isFirebaseEnabled) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.GOALS, JSON.stringify(goals));
      localStorage.setItem(LOCAL_STORAGE_KEYS.STATS, JSON.stringify(stats));
    }
  }, [goals, stats]);
  const addGoal = async (goal: Omit<Goal, 'id' | 'completed' | 'progress' | 'createdAt' | 'userId'>) => {
    if (!user) return;
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      const newGoal: Goal = {
        ...goal,
        id: `goal-${Date.now()}`,
        userId: user.uid,
        completed: false,
        progress: 0,
        createdAt: new Date().toISOString()
      };
      setGoals(prev => [newGoal, ...prev]);
      notifications.success('Quest created!', 'Your new quest has been added');
      return;
    }
    try {
      await addDoc(collection(db, 'goals'), {
        ...goal,
        userId: user.uid,
        completed: false,
        progress: 0,
        createdAt: serverTimestamp()
      });
      notifications.success('Quest created!', 'Your new quest has been added');
    } catch (error: any) {
      notifications.error('Failed to create quest', error.message);
      throw error;
    }
  };
  const updateGoal = async (id: string, updates: Partial<Goal>) => {
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      setGoals(prev => prev.map(g => g.id === id ? {
        ...g,
        ...updates
      } : g));
      notifications.success('Quest updated!', 'Your changes have been saved');
      return;
    }
    try {
      await updateDoc(doc(db, 'goals', id), updates);
      notifications.success('Quest updated!', 'Your changes have been saved');
    } catch (error: any) {
      notifications.error('Failed to update quest', error.message);
      throw error;
    }
  };
  const deleteGoal = async (id: string) => {
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      setGoals(prev => prev.filter(g => g.id !== id));
      notifications.success('Quest deleted!', 'The quest has been removed');
      return;
    }
    try {
      await deleteDoc(doc(db, 'goals', id));
      notifications.success('Quest deleted!', 'The quest has been removed');
    } catch (error: any) {
      notifications.error('Failed to delete quest', error.message);
      throw error;
    }
  };
  const toggleGoal = async (id: string) => {
    if (!user) return;
    const goal = goals.find(g => g.id === id);
    if (!goal) return;
    const newCompleted = !goal.completed;
    const xpChange = newCompleted ? 50 : -50;
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      setGoals(prev => prev.map(g => g.id === id ? {
        ...g,
        completed: newCompleted,
        progress: newCompleted ? 100 : 0
      } : g));
      const newXP = stats.xp + xpChange;
      const newLevel = Math.floor(newXP / 1000) + 1;
      const newCompletedCount = stats.completedGoals + (newCompleted ? 1 : -1);
      setStats(prev => ({
        ...prev,
        xp: newXP,
        level: newLevel,
        completedGoals: newCompletedCount
      }));
      if (newLevel > stats.level) {
        notifications.levelUp(newLevel);
      }
      await checkAchievements(newCompletedCount);
      return;
    }
    try {
      // Update goal
      await updateDoc(doc(db, 'goals', id), {
        completed: newCompleted,
        progress: newCompleted ? 100 : 0
      });
      // Update user stats
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        xp: increment(xpChange),
        completedGoals: increment(newCompleted ? 1 : -1)
      });
      // Check for level up
      const newXP = stats.xp + xpChange;
      const newLevel = Math.floor(newXP / 1000) + 1;
      if (newLevel > stats.level) {
        await updateDoc(userRef, {
          level: newLevel
        });
        notifications.levelUp(newLevel);
      }
      // Check for achievements
      if (newCompleted) {
        await checkAchievements(stats.completedGoals + 1);
      }
    } catch (error: any) {
      notifications.error('Failed to update quest', error.message);
      throw error;
    }
  };
  const addXP = async (amount: number) => {
    if (!user) return;
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      const newXP = stats.xp + amount;
      const newLevel = Math.floor(newXP / 1000) + 1;
      setStats(prev => ({
        ...prev,
        xp: newXP,
        level: newLevel
      }));
      if (newLevel > stats.level) {
        notifications.levelUp(newLevel);
      }
      return;
    }
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        xp: increment(amount)
      });
      // Check for level up
      const newXP = stats.xp + amount;
      const newLevel = Math.floor(newXP / 1000) + 1;
      if (newLevel > stats.level) {
        await updateDoc(userRef, {
          level: newLevel
        });
        notifications.levelUp(newLevel);
      }
    } catch (error: any) {
      notifications.error('Failed to add XP', error.message);
      throw error;
    }
  };
  const completeFocusSession = async (duration: number) => {
    if (!user) return;
    const xpReward = Math.floor(duration / 5) * 10;
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      const newXP = stats.xp + xpReward;
      const newLevel = Math.floor(newXP / 1000) + 1;
      setStats(prev => ({
        ...prev,
        xp: newXP,
        level: newLevel,
        focusSessions: prev.focusSessions + 1,
        totalFocusMinutes: prev.totalFocusMinutes + duration
      }));
      notifications.focusComplete(duration, xpReward);
      await checkAchievements(stats.completedGoals, stats.focusSessions + 1, stats.totalFocusMinutes + duration);
      return;
    }
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        xp: increment(xpReward),
        focusSessions: increment(1),
        totalFocusMinutes: increment(duration)
      });
      notifications.focusComplete(duration, xpReward);
      // Check for achievements
      await checkAchievements(stats.completedGoals, stats.focusSessions + 1, stats.totalFocusMinutes + duration);
    } catch (error: any) {
      notifications.error('Failed to complete session', error.message);
      throw error;
    }
  };
  const unlockAchievement = async (achievementId: string) => {
    if (!user || stats.achievements.includes(achievementId)) return;
    if (!isFirebaseEnabled || !db) {
      // Local-only mode
      setStats(prev => ({
        ...prev,
        achievements: [...prev.achievements, achievementId],
        xp: prev.xp + 100
      }));
      notifications.success('🏆 Achievement Unlocked!', getAchievementName(achievementId));
      return;
    }
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const currentAchievements = userDoc.data()?.achievements || [];
      await updateDoc(userRef, {
        achievements: [...currentAchievements, achievementId],
        xp: increment(100)
      });
      notifications.success('🏆 Achievement Unlocked!', getAchievementName(achievementId));
    } catch (error: any) {
      notifications.error('Failed to unlock achievement', error.message);
      throw error;
    }
  };
  const checkAchievements = async (completedGoals: number, focusSessions?: number, totalMinutes?: number) => {
    const sessions = focusSessions !== undefined ? focusSessions : stats.focusSessions;
    const minutes = totalMinutes !== undefined ? totalMinutes : stats.totalFocusMinutes;
    // First Quest
    if (completedGoals >= 1 && !stats.achievements.includes('first_quest')) {
      await unlockAchievement('first_quest');
    }
    // Quest Master (10 quests)
    if (completedGoals >= 10 && !stats.achievements.includes('quest_master')) {
      await unlockAchievement('quest_master');
    }
    // Century (100 quests)
    if (completedGoals >= 100 && !stats.achievements.includes('century')) {
      await unlockAchievement('century');
    }
    // Focus Beginner (5 sessions)
    if (sessions >= 5 && !stats.achievements.includes('focus_beginner')) {
      await unlockAchievement('focus_beginner');
    }
    // Focus Master (50 sessions)
    if (sessions >= 50 && !stats.achievements.includes('focus_master')) {
      await unlockAchievement('focus_master');
    }
    // Time Lord (1000 minutes)
    if (minutes >= 1000 && !stats.achievements.includes('time_lord')) {
      await unlockAchievement('time_lord');
    }
  };
  const getAchievementName = (id: string): string => {
    const names: Record<string, string> = {
      first_quest: 'First Quest Complete',
      quest_master: 'Quest Master - 10 Quests',
      century: 'Century - 100 Quests',
      focus_beginner: 'Focus Beginner - 5 Sessions',
      focus_master: 'Focus Master - 50 Sessions',
      time_lord: 'Time Lord - 1000 Minutes',
      early_bird: 'Early Bird',
      night_owl: 'Night Owl',
      streak_warrior: '7-Day Streak',
      streak_legend: '30-Day Streak'
    };
    return names[id] || 'Achievement Unlocked';
  };
  const value = {
    goals,
    stats,
    loading,
    addGoal,
    updateGoal,
    deleteGoal,
    toggleGoal,
    addXP,
    completeFocusSession,
    unlockAchievement
  };
  return <UserContext.Provider value={value} data-id="element-605">{children}</UserContext.Provider>;
}
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}